import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import Sidebar from "@/components/propgen/Sidebar";
import { api, formatApiError } from "@/lib/propgen_api";
import { useAuth } from "@/lib/propgen_auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, ShieldCheck, ShieldOff } from "lucide-react";

const ROLE_INFO = {
  admin: { label: "Admin", internal: true, color: "var(--peak-blue)", desc: "Everything · manage users" },
  delivery_lead: { label: "Delivery Lead", internal: true, color: "var(--peak-blue)", desc: "All docs + internal cost" },
  sales_lead: { label: "Sales Lead", internal: false, color: "#111", desc: "Client-facing docs only" },
  reviewer: { label: "Reviewer", internal: false, color: "#666", desc: "Read + comment on client docs" },
};

export default function AdminUsers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (user === null) return <Navigate to="/login" replace />;
  if (user && user.is_admin === false) return <Navigate to="/superadmin/propgen" replace />;

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.email} → ${role}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (u.id === user.id) { toast.error("Cannot delete your own account"); return; }
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-users-page">
      <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
      <main className="flex-1 min-w-0">
        <div className="border-b bg-card">
          <div className="px-8 py-6 flex items-end justify-between gap-6">
            <div>
              <div className="eyebrow flex items-center gap-2"><Users className="w-3 h-3" /> Admin / Users</div>
              <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Team members</h1>
              <p className="text-sm text-muted-foreground mt-1">Only admins see this page. Roles gate access to internal docs & internal build costs.</p>
            </div>
            <Button
              onClick={() => setShowNew(true)}
              data-testid="admin-new-user-btn"
              className="h-10 bg-black hover:bg-[var(--peak-blue)] text-white font-display font-bold uppercase text-xs tracking-wide transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> New user
            </Button>
          </div>
        </div>

        <div className="p-8">
          <div className="border bg-card" data-testid="users-table">
            <div className="grid grid-cols-[1fr_1fr_160px_120px_60px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
              <div>Email</div><div>Name</div><div>Role</div><div className="text-center">Internal access</div><div></div>
            </div>
            {loading ? (
              <div className="p-8 text-sm text-muted-foreground">Loading…</div>
            ) : users.length === 0 ? (
              <div className="p-8 text-sm text-muted-foreground">No users.</div>
            ) : users.map((u) => {
              const info = ROLE_INFO[u.role] || ROLE_INFO.reviewer;
              return (
                <div key={u.id} className="grid grid-cols-[1fr_1fr_160px_120px_60px] items-center px-4 py-3 border-b last:border-b-0 text-sm" data-testid={`user-row-${u.id}`}>
                  <div className="font-mono text-xs truncate">{u.email}</div>
                  <div className="truncate">{u.name || <span className="text-muted-foreground italic">—</span>}</div>
                  <div>
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === user.id}>
                      <SelectTrigger data-testid={`user-role-${u.id}`} className="h-9 border-2 text-xs font-mono uppercase" style={{ borderColor: info.color }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_INFO).map(([k, r]) => (
                          <SelectItem key={k} value={k}>
                            <span className="font-mono text-xs uppercase">{r.label}</span>
                            <span className="text-[10px] text-muted-foreground ml-2">· {r.desc}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-center">
                    {u.can_see_internal
                      ? <ShieldCheck className="w-4 h-4 text-[var(--peak-blue)] inline" />
                      : <ShieldOff className="w-4 h-4 text-neutral-400 inline" />}
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => remove(u)}
                      disabled={u.id === user.id}
                      data-testid={`user-delete-${u.id}`}
                      className="text-muted-foreground hover:text-red-600 disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <NewUserDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function NewUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("reviewer");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users", { email, name, password, role });
      toast.success("User created");
      setEmail(""); setName(""); setPassword(""); setRole("reviewer");
      onCreated();
    } catch (ex) { toast.error(formatApiError(ex)); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Create user</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="eyebrow block mb-1.5">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="new-user-email" className="h-10 border-2 font-mono text-sm" />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Name (optional)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="h-10 border-2 text-sm" />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Password</label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={6} data-testid="new-user-password" className="h-10 border-2 font-mono text-sm" />
          </div>
          <div>
            <label className="eyebrow block mb-1.5">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="new-user-role" className="h-10 border-2 text-sm font-mono uppercase"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_INFO).map(([k, r]) => (
                  <SelectItem key={k} value={k}>
                    <span className="font-mono uppercase">{r.label}</span>
                    <span className="text-[10px] text-muted-foreground ml-2">· {r.desc}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-wider">
              {ROLE_INFO[role]?.internal
                ? <span className="text-[var(--peak-blue)]">✓ Will see internal docs + build costs</span>
                : <span className="text-muted-foreground">Client-facing docs only · no internal access</span>}
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors" data-testid="new-user-cancel">Cancel</button>
            <Button type="submit" disabled={loading || !email || !password} data-testid="new-user-submit" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide">
              {loading ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
